import { LayerMetadataRecord, LayerPlusScenarios, MapLayer, RiskWorkspace } from '@app/types'
import { getBaseLayerId } from '@app/domains/layers/utils/layers.utils'
import {
  CapitalLayer,
  ContextualLayer,
  LiveLayer,
  ScenarioLayer,
  TemplateGroup,
  TemplateSet,
  Workspace,
} from '@app/modules/Risk/shared/types/riskWorkspace.types'
import {
  formatParamLabel,
  formatParamValue,
  ScenarioGrid,
} from '@app/domains/hazards/utils/hazardScenarioGrid.utils'
import { isMeaningfulParamValue } from '@app/domains/hazards/utils/hazard.constants'
import { HazardScenario } from '@app/domains/hazards/types/hazard.types'
import { isCapitalSetPreset } from './workspace.constants'

// The API only persists flat layer id lists (workspace.layers), stripped of everything used to render them (name, events, groupings).
// This hydrates the layers back to the workspace form to be used within workspaces
export const hydrateWorkspaceLayers = (
  workspace: RiskWorkspace,
  hazardLayers: LayerPlusScenarios[],
  elementLayers: LayerMetadataRecord[],
  contextualLayers: LayerMetadataRecord[],
  liveLayers: LayerMetadataRecord[],
): Pick<Workspace, 'scenario_sets' | 'capital_sets' | 'contextual_layers' | 'live_groups'> => {
  type LayerMin = { id: string; events?: string[] }
  type RawGroup = { name: string; layers: LayerMin[] }
  type RawSet = { name: string; year?: number; groups: RawGroup[] }

  const hydratedLayers = (
    layers: LayerMin[] | undefined,
    type: 'scenario_sets' | 'capital_sets' | 'live_groups' | 'contextual_layers',
  ): (ScenarioLayer | CapitalLayer | LiveLayer | ContextualLayer)[] => {
    if (!layers?.length) return []

    return layers.map((layer) => {
      if (type === 'scenario_sets') {
        return {
          id: layer.id,
          events: layer.events ?? [],
          layer: hazardLayers.find((fLayer) => fLayer.id === layer.id) as LayerPlusScenarios,
        }
      }
      if (type === 'capital_sets') {
        return {
          id: layer.id,
          layer: elementLayers.find((fLayer) => fLayer.id === layer.id) as LayerPlusScenarios,
        }
      }
      if (type === 'live_groups') {
        return {
          id: layer.id,
          layer: liveLayers.find((lLayer) => lLayer.id === layer.id) as LayerPlusScenarios,
        }
      }
      return {
        id: layer.id,
        layer: contextualLayers.find((cLayer) => cLayer.id === layer.id) as LayerPlusScenarios,
      }
    })
  }

  const hydratedGroups = (
    groups: RawGroup[] | undefined,
    type: 'scenario_sets' | 'capital_sets' | 'live_groups',
  ): TemplateGroup[] => {
    if (!groups?.length) return []

    return groups
      .filter((group) => group.layers?.length)
      .map((group) => ({
        name: group.name,
        layers: hydratedLayers(group.layers, type) as (ScenarioLayer | CapitalLayer | LiveLayer)[],
      }))
  }

  const hydratedSets = (type: 'scenario_sets' | 'capital_sets'): TemplateSet[] => {
    const sets = workspace[type] as RawSet[] | undefined
    if (!sets?.length) return []

    return sets
      .filter((set) => set.groups?.length)
      .map((set) => ({
        name: set.name,
        ...(set.year !== undefined && { year: set.year }),
        groups: hydratedGroups(set.groups, type),
      }))
  }

  // Element layers misfiled under contextual_layers move into the first custom capital set
  const elementIds = elementLayers.map(({ id }) => id)
  const misfiled = getMisfiledElementLayers(workspace, elementIds)
  const capitalSets = hydratedSets('capital_sets')
  if (misfiled.length) {
    let target = capitalSets.find((set) => !isCapitalSetPreset(set.name))
    if (!target) {
      target = { name: 'Elements', groups: [] }
      capitalSets.unshift(target)
    }

    // grouped by the layer's category, e.g. `Cascading Impacts`
    hydratedLayers(misfiled, 'capital_sets').forEach((layer) => {
      const name = layer.layer?.categories?.[0]?.display_name ?? 'Elements'
      const group = target.groups.find((g) => g.name === name)
      if (group) group.layers.push(layer as CapitalLayer)
      else target.groups.push({ name, layers: [layer as CapitalLayer] })
    })
  }

  return {
    scenario_sets: hydratedSets('scenario_sets'),
    capital_sets: capitalSets,
    contextual_layers: hydratedLayers(
      ((workspace.contextual_layers ?? []) as LayerMin[]).filter(
        ({ id }) => !elementIds.includes(id),
      ),
      'contextual_layers',
    ) as ContextualLayer[],
    live_groups: hydratedGroups(workspace.live_groups as RawGroup[] | undefined, 'live_groups'),
  }
}

// By catalog type, as some element layers have ids starting with `i` too (e.g. `iso48`)
export const isContextualLayer = (layer: LayerMetadataRecord): boolean =>
  layer.type === 'contextual'

// Anything not a hazard or contextual, so element ids not starting with `e` (e.g. `iso48`) are kept
export const isElementLayer = (layer: LayerMetadataRecord): boolean =>
  !layer.id.startsWith('h') && !isContextualLayer(layer)

// Older workspaces saved events with the version (`l0_v9`)
export const stripEventVersion = (event: string): string => event.replace(/_v\d+$/, '')

// Asset tags are `${layerId}_${event}_v${version}`; workspaces store only the unversioned event
export const assetTagToEvent = (layerId: string, assetTag: string): string => {
  const prefix = `${layerId}_`
  const event = assetTag.startsWith(prefix) ? assetTag.slice(prefix.length) : assetTag
  return stripEventVersion(event)
}

// Builds workspace form layers from the layers currently on the risk map
export const mapLayersToWorkspaceLayers = (
  mapLayers: MapLayer[],
  hazardLayers: LayerPlusScenarios[],
  elementLayers: LayerMetadataRecord[],
  contextualLayers: LayerMetadataRecord[],
  liveLayers: LayerMetadataRecord[],
): {
  layers: Pick<Workspace, 'scenario_sets' | 'capital_sets' | 'contextual_layers' | 'live_groups'>
  unresolved: MapLayer[]
} => {
  const unresolved: MapLayer[] = []

  // map layers carry their id in different fields, with suffixes the catalog ids don't (e.g. `e12_l0`)
  const candidateIds = (mapLayer: MapLayer) =>
    [mapLayer.type, mapLayer.assetTag, mapLayer.id]
      .filter(Boolean)
      .flatMap((id) => [id, getBaseLayerId(id)])

  const resolveLayers = (layerType: MapLayer['layerType'], options: LayerMetadataRecord[]) => {
    const resolved = new Map<string, CapitalLayer>()
    mapLayers
      .filter((mapLayer) => mapLayer.layerType === layerType)
      .forEach((mapLayer) => {
        const ids = candidateIds(mapLayer)
        const layer = options.find((option) => ids.includes(option.id))
        if (!layer) return unresolved.push(mapLayer)
        resolved.set(layer.id, { id: layer.id, layer: layer as LayerPlusScenarios })
      })
    return [...resolved.values()]
  }

  // one form layer per hazard, with each scenario on the map as an event
  const scenarioLayers = new Map<string, ScenarioLayer>()
  mapLayers
    .filter((mapLayer) => mapLayer.layerType === 'hazard')
    .forEach((mapLayer) => {
      const layer = hazardLayers.find(
        ({ id }) =>
          (mapLayer.hazard_id && id === `h${mapLayer.hazard_id}`) ||
          mapLayer.assetTag?.startsWith(`${id}_`),
      )
      if (!layer) return unresolved.push(mapLayer)
      const event = assetTagToEvent(layer.id, mapLayer.assetTag)
      const existing = scenarioLayers.get(layer.id)
      if (!existing) {
        scenarioLayers.set(layer.id, { id: layer.id, events: [event], layer })
      } else if (!existing.events.includes(event)) {
        existing.events.push(event)
      }
    })

  const toSets = (name: string, layers: (ScenarioLayer | CapitalLayer)[]): TemplateSet[] =>
    layers.length ? [{ name, groups: [{ name, layers }] }] : []

  const live = resolveLayers('live', liveLayers)

  return {
    layers: {
      scenario_sets: toSets('Hazards', [...scenarioLayers.values()]),
      capital_sets: toSets('Capitals', resolveLayers('asset', elementLayers)),
      contextual_layers: resolveLayers('information', contextualLayers),
      live_groups: live.length ? [{ name: 'Live layers', layers: live }] : [],
    },
    unresolved,
  }
}

// Matches the ids stored in workspace.layers
export const mapLayerKey = (layer: MapLayer) => {
  if (layer.layerType === 'asset') return layer.type
  if (layer.layerType === 'hazard') return layer.assetTag
  return layer.id
}

// Whether the map's layers or regions differ from the workspace's layer ids
export const workspaceDiffersFromMap = (
  workspace: RiskWorkspace,
  mapLayers: MapLayer[],
  regionCodes: string[],
  layerIds: Record<string, string[]> = workspace.layers,
): boolean => {
  const toKey = (ids: string[]) => [...new Set(ids)].sort().join('|')

  const workspaceLayerIds = Object.values(layerIds ?? {}).flat()
  return (
    toKey(workspaceLayerIds) !== toKey(mapLayers.map(mapLayerKey)) ||
    toKey(workspace.regions ?? []) !== toKey(regionCodes)
  )
}

// Loose shape shared by API and form sets
type SetLike = { groups?: { layers?: { id?: string; events?: string[] }[] }[] }

const uniqueSetLayers = (sets: SetLike[]) => {
  const seen = new Set<string>()
  return sets.flatMap((set) =>
    (set.groups ?? []).flatMap((group) =>
      (group.layers ?? []).flatMap((layer) => {
        if (!layer.id || seen.has(layer.id)) return []
        seen.add(layer.id)
        return [{ id: layer.id, events: layer.events ?? [] }]
      }),
    ),
  )
}

// Risk map asset ids for capital sets, resolving the version (e.g. `e16_l0_v8`) when available
export const capitalSetsToAssetIds = (
  sets: SetLike[],
  availableAssetIds: string[] = [],
): string[] =>
  uniqueSetLayers(sets).map(
    ({ id }) => resolveEventAssetTag(id, 'l0', availableAssetIds) ?? `${id}_l0`,
  )

// Risk map asset tags for scenario sets, first event per hazard only
export const scenarioSetsToAssetTags = (sets: SetLike[], availableAssetTags: string[]): string[] =>
  uniqueSetLayers(sets).flatMap(({ id, events: [firstEvent] }) =>
    firstEvent
      ? [resolveEventAssetTag(id, firstEvent, availableAssetTags) ?? `${id}_${firstEvent}`]
      : [],
  )

// Older workspaces filed element layers with `i` ids (e.g. `iso48`) under contextual_layers
const getMisfiledElementLayers = (
  workspace: RiskWorkspace,
  elementLayerIds: string[],
): { id: string }[] =>
  ((workspace.contextual_layers ?? []) as { id: string }[]).filter(({ id }) =>
    elementLayerIds.includes(id),
  )

export const workspaceHasSets = (workspace: RiskWorkspace): boolean =>
  !!(workspace.scenario_sets?.length || workspace.capital_sets?.length)

// The workspace's layer ids for the selected scenario and capital sets
export const getWorkspaceSetLayerIds = (
  workspace: RiskWorkspace,
  scenarioSetIndex: number | null,
  capitalSetIndex: number | null,
  availableAssetTags: string[],
  availableAssetIds: string[],
  elementLayerIds: string[] = [],
): RiskWorkspace['layers'] => {
  if (!workspaceHasSets(workspace)) return workspace.layers

  const scenarioSet = scenarioSetIndex === null ? null : workspace.scenario_sets[scenarioSetIndex]
  const capitalSet = capitalSetIndex === null ? null : workspace.capital_sets[capitalSetIndex]
  const misfiledSet = { groups: [{ layers: getMisfiledElementLayers(workspace, elementLayerIds) }] }

  return {
    scenarios: scenarioSet ? scenarioSetsToAssetTags([scenarioSet], availableAssetTags) : [],
    assets: capitalSetsToAssetIds(
      [...(capitalSet ? [capitalSet] : []), misfiledSet],
      availableAssetIds,
    ),
    contexts: workspace.layers?.contexts ?? [],
    live: workspace.layers?.live ?? [],
  }
}

// Finds the available id for a saved one, whatever its version (e.g. `i5_l0` -> `i5_l0_v3`)
export const resolveVersionedLayerId = (id: string, availableIds: string[]): string | undefined => {
  if (availableIds.includes(id)) return id
  const base = stripEventVersion(id)
  return availableIds.find((available) => available === base || available.startsWith(`${base}_v`))
}

// Finds the asset tag for an event among the available ones, whatever its version
export const resolveEventAssetTag = (
  layerId: string,
  event: string,
  assetTags: string[],
): string | undefined => {
  const base = `${layerId}_${stripEventVersion(event)}`
  return assetTags.find((tag) => tag === base || tag.startsWith(`${base}_v`))
}

export const formatScenarioEventLabel = (scenario: HazardScenario): string =>
  Object.entries(scenario.parameters)
    .filter(([, value]) => isMeaningfulParamValue(value))
    .map(
      ([key, value]) => `${formatParamLabel(key).toUpperCase()}: ${formatParamValue(key, value)}`,
    )
    .join(' / ')

export const sortAssetTagsByGrid = (assetTags: string[], grid: ScenarioGrid): string[] => {
  const gridOrder = new Map(grid.grid.flat(2).map((cell, index) => [cell.assetTag, index] as const))
  const orderOf = (assetTag: string) => gridOrder.get(assetTag) ?? Number.POSITIVE_INFINITY

  return [...assetTags].sort((a, b) => orderOf(a) - orderOf(b))
}
